import { useMemo, useState } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import type { CatalogCategoryKindType, DirCatalogCategoryType } from '@econtractor/types';
import { useTheme } from 'context/ThemeContext';
import { useSettings } from 'context/SettingsContext';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import SolarIcon from 'components/icons/SolarIcon';
import EToolTip from 'components/others/EToolTip';
import { CATALOG_DIR_I18N_NS } from './model/catalogDirApi';
import type { CatalogSelectOption } from './types';

interface CatalogCategoryFilterProps {
    kind: CatalogCategoryKindType;
    filterCategoryId: number | null;
    setFilterCategoryId: (id: number | null) => void;
    embedded?: boolean;
}

const DIRS_KEY: Record<CatalogCategoryKindType, 'ProductCategory' | 'ProductSetCategory' | 'ServiceCategory'> = {
    product: 'ProductCategory',
    set: 'ProductSetCategory',
    service: 'ServiceCategory',
};

const CatalogCategoryFilterSelect = ({
    kind,
    filterCategoryId,
    setFilterCategoryId,
}: Omit<CatalogCategoryFilterProps, 'embedded'>) => {
    const ns = CATALOG_DIR_I18N_NS[kind];
    const { t } = useTranslation([ns, 'common']);
    const { darkTheme } = useTheme();
    const { localSettings } = useSettings();
    const selectTheme = darkTheme ? selectStylesDark : selectStylesLigth;

    const categoryOptions = useMemo<CatalogSelectOption[]>(
        () =>
            ((localSettings?.DIRS?.[DIRS_KEY[kind]] || []) as DirCatalogCategoryType[])
                .filter((item) => !item.hidden || item.ID === filterCategoryId)
                .map((item) => ({ value: item.ID, label: item.categoryName })),
        [kind, localSettings?.DIRS, filterCategoryId],
    );

    return (
        <>
            <label className="form-label text-muted fs-12 mb-1">{t('filterCategory')}</label>
            <Select<CatalogSelectOption, false>
                isClearable
                options={categoryOptions}
                value={categoryOptions.find((opt) => opt.value === filterCategoryId) || null}
                onChange={(opt) => setFilterCategoryId(opt?.value ? Number(opt.value) : null)}
                placeholder={t('filterCategory')}
                className="w-100"
                styles={{
                    ...selectTheme,
                    menuPortal: (base) => ({ ...base, zIndex: 2100 }),
                }}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
            />
        </>
    );
};

const CatalogCategoryFilter = ({
    kind,
    filterCategoryId,
    setFilterCategoryId,
    embedded = false,
}: CatalogCategoryFilterProps) => {
    const ns = CATALOG_DIR_I18N_NS[kind];
    const { t } = useTranslation([ns, 'common']);
    const [show, setShow] = useState(false);
    const [draftCategoryId, setDraftCategoryId] = useState<number | null>(filterCategoryId);
    const filterActive = Boolean(filterCategoryId);

    const handleHide = () => setShow(false);

    const handleOpen = () => {
        setDraftCategoryId(filterCategoryId);
        setShow(true);
    };

    if (embedded) {
        return (
            <div className="mb-3">
                <CatalogCategoryFilterSelect
                    kind={kind}
                    filterCategoryId={filterCategoryId}
                    setFilterCategoryId={setFilterCategoryId}
                />
            </div>
        );
    }

    return (
        <>
            <EToolTip title={t('toolFilter')}>
                <span className="d-inline-block">
                    <Button
                        className="fs-14 px-2 ms-1"
                        size="sm"
                        variant={filterActive ? 'danger' : 'primary'}
                        onClick={(e) => {
                            e.currentTarget.blur();
                            handleOpen();
                        }}
                    >
                        <SolarIcon name="funnel" className="fs-14 p-0" />
                    </Button>
                </span>
            </EToolTip>

            <Offcanvas show={show} onHide={handleHide} placement="end">
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title>{t('toolFilter')}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                    <div className="off-filter-form d-flex flex-column h-100">
                        <div className="off-filter-content px-3">
                            <div className="mb-3">
                                <CatalogCategoryFilterSelect
                                    kind={kind}
                                    filterCategoryId={draftCategoryId}
                                    setFilterCategoryId={setDraftCategoryId}
                                />
                            </div>
                        </div>
                        <div className="p-3 offcanvas-footer off-filter-footer">
                            <div className="d-flex justify-content-end gap-2">
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setFilterCategoryId(null);
                                        handleHide();
                                    }}
                                >
                                    {t('common:buttons.reset')}
                                </Button>
                                <Button
                                    variant="info"
                                    className="min100px"
                                    onClick={() => {
                                        setFilterCategoryId(draftCategoryId);
                                        handleHide();
                                    }}
                                >
                                    {t('common:buttons.apply')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default CatalogCategoryFilter;
